import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { ArrowLeft, Search, Pencil, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getInstamojoSession, clearInstamojoSession } from './InstamojoPasswordGate';

const PAGE_SIZES = [25, 50, 100, 200];

export default function InstamojoPurposes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [size, setSize]       = useState(50);
  const [list, setList]       = useState({ total: 0, total_pages: 0, results: [] });

  // Session check
  useEffect(() => {
    if (!getInstamojoSession()) navigate('/instamojo', { replace: true });
  }, [navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/instamojo-archive/purposes', {
        params: { search: search || undefined, page, size },
      });
      setList(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        clearInstamojoSession();
        toast.error('Session expired.');
        navigate('/instamojo', { replace: true });
        return;
      }
      toast.error(err.response?.data?.detail || 'Failed to load purposes.');
    } finally {
      setLoading(false);
    }
  }, [search, page, size, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleActive = async (row) => {
    const willEnable = row.is_active === 0;
    const msg = willEnable
      ? 'Are you sure you want to enable the payment link?'
      : 'Are you sure you want to disable the payment link?';
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/instamojo-archive/purposes/${row.id}/active`, {
        isActive: willEnable ? 1 : 0,
      });
      toast.success(willEnable ? 'Enabled.' : 'Disabled.');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update.');
    }
  };

  const totalPages = list.total_pages || 1;
  const startIdx = list.total === 0 ? 0 : (page - 1) * size + 1;
  const endIdx   = Math.min(page * size, list.total);

  return (
    <div className="min-h-screen bg-slate-50 p-3 md:p-4">
      <div className="w-full space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => navigate('/instamojo/list')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-bold text-slate-900">Purpose listing</h1>
          </div>
        </div>

        {/* Search */}
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <Label>Search Purpose</Label>
              <div className="flex gap-2">
                <Input value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (setPage(1), fetchData())}
                  placeholder="Enter Purpose" />
                <Button onClick={() => { setPage(1); fetchData(); }}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table
              className="w-full text-sm table-fixed border-collapse
                         [&_th]:border [&_th]:border-slate-300 [&_th]:px-2 [&_th]:py-2 [&_th]:font-semibold [&_th]:text-slate-700
                         [&_td]:border [&_td]:border-slate-200 [&_td]:px-2 [&_td]:py-2 [&_td]:align-middle
                         [&_tbody_tr:nth-child(even)]:bg-slate-50 [&_tbody_tr:hover]:bg-violet-50/60">
              <colgroup>
                <col style={{ width: '8%'  }} />   {/* Sr. No. */}
                <col style={{ width: '60%' }} />   {/* Title */}
                <col style={{ width: '20%' }} />   {/* Active/Inactive */}
                <col style={{ width: '12%' }} />   {/* Edit */}
              </colgroup>
              <thead className="bg-slate-100 text-left">
                <tr>
                  <th>Sr. No.</th>
                  <th>Title</th>
                  <th className="!text-center">Active/Inactive</th>
                  <th className="!text-center">Edit</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={4} className="!text-center !py-6 text-slate-500">
                    <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Loading…
                  </td></tr>
                )}
                {!loading && list.results.length === 0 && (
                  <tr><td colSpan={4} className="!text-center !py-6 text-slate-500">No purposes found.</td></tr>
                )}
                {!loading && list.results.map((row, i) => (
                  <tr key={row.id}>
                    <td className="text-slate-600 text-center">{startIdx + i}</td>
                    <td className="truncate" title={row.title || ''}>{row.title || '—'}</td>
                    <td className="text-center">
                      <button type="button"
                        onClick={() => toggleActive(row)}
                        className={`text-sm font-semibold underline-offset-2 hover:underline ${
                          row.is_active === 1 ? 'text-emerald-700' : 'text-red-700'
                        }`}>
                        {row.is_active === 1 ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="text-center">
                      <button type="button"
                        onClick={() => navigate(`/instamojo/purpose/${row.id}/bank-details`)}
                        title="Edit Bank Details"
                        className="p-1 rounded hover:bg-slate-200 text-slate-600">
                        <Pencil className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div className="flex items-center justify-between flex-wrap gap-2 p-3 border-t bg-slate-50 text-sm">
            <div className="text-slate-600">
              Showing <span className="font-medium">{startIdx}</span>–<span className="font-medium">{endIdx}</span> of{' '}
              <span className="font-medium">{list.total.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Per page:</span>
              <select value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }}
                className="h-8 rounded-md border border-slate-200 px-2">
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(1)}>«</Button>
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>‹</Button>
              <span className="px-2">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>›</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
